/**
 * Sidebar group-divider layout helpers.
 *
 * The per-user sidebar ordering (`preferences.sidebarOrder`, see
 * `useSidebarOrder`) is a flat array of nav `to` paths. To let users split the
 * nav into named groups, we interleave *divider tokens* among those paths:
 *
 *   ["/products", "__divider:a1b2c3:Tools", "/users", "/bug"]
 *
 * A divider token carries its own id (stable across renames) and a
 * URL-encoded label, so the whole feature rides on the existing order array —
 * no new preference field, schema change, or migration. Real nav paths and
 * divider tokens share one ordered, de-duplicated list, which the backend
 * already validates (unique strings, ≤128 chars each).
 */

/** Marker prefix for a divider entry inside the sidebar order array. */
export const DIVIDER_PREFIX = '__divider:';

/** A rendered divider (named group header). */
export interface DividerNode {
  type: 'divider';
  /** Stable id — unchanged when the label is edited. */
  id: string;
  /** User-visible group name (may be empty for an unnamed divider). */
  label: string;
  /** The token as it appears in the order array — used as the drag/React key. */
  key: string;
}

/** A rendered nav item. */
export interface ItemNode<T> {
  type: 'item';
  item: T;
  /** The nav `to` path — used as the drag/React key. */
  key: string;
}

export type LayoutNode<T> = ItemNode<T> | DividerNode;

/** Max characters allowed in a group label (keeps the encoded token well under the 128-char order-entry cap). */
export const MAX_DIVIDER_LABEL_LENGTH = 40;

/** True when an order-array key denotes a divider rather than a nav path. */
export function isDividerKey(key: string): boolean {
  return key.startsWith(DIVIDER_PREFIX);
}

/**
 * Generate a stable divider id. Prefers `crypto.randomUUID`; falls back for
 * older/non-secure contexts. Uniqueness within one user's sidebar is all we need.
 */
export function makeDividerId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().slice(0, 8);
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

/** Build a divider token from an id and label. */
export function encodeDivider(id: string, label: string): string {
  return `${DIVIDER_PREFIX}${id}:${encodeURIComponent(label)}`;
}

/**
 * Parse a divider token into `{ id, label }`. Returns null for a non-divider
 * key. Tolerant of a token with no label separator (treats it as unnamed).
 */
export function parseDivider(key: string): { id: string; label: string } | null {
  if (!isDividerKey(key)) return null;
  const rest = key.slice(DIVIDER_PREFIX.length);
  const sep = rest.indexOf(':');
  const id = sep === -1 ? rest : rest.slice(0, sep);
  const rawLabel = sep === -1 ? '' : rest.slice(sep + 1);
  let label: string;
  try {
    label = decodeURIComponent(rawLabel);
  } catch {
    label = rawLabel;
  }
  return { id, label };
}

/**
 * Merge nav items with a saved order into a renderable node list.
 *
 * Walks `order`, emitting a divider node for each divider token and an item
 * node for each known nav path (first occurrence only). Nav items absent from
 * `order` — e.g. a newly-shipped nav entry — are appended at the end so they
 * always surface rather than vanish (mirrors `applyOrder`). Stale nav paths and
 * duplicate keys in `order` are dropped.
 */
export function buildSidebarLayout<T>(
  items: T[],
  order: string[],
  getKey: (item: T) => string,
): LayoutNode<T>[] {
  const byKey = new Map(items.map((i) => [getKey(i), i]));
  const used = new Set<string>();
  const nodes: LayoutNode<T>[] = [];

  for (const key of order) {
    if (isDividerKey(key)) {
      const divider = parseDivider(key)!;
      nodes.push({ type: 'divider', id: divider.id, label: divider.label, key });
      continue;
    }
    const item = byKey.get(key);
    if (item !== undefined && !used.has(key)) {
      used.add(key);
      nodes.push({ type: 'item', item, key });
    }
  }

  for (const item of items) {
    const key = getKey(item);
    if (!used.has(key)) {
      used.add(key);
      nodes.push({ type: 'item', item, key });
    }
  }

  return nodes;
}

/** A run of items under an optional leading group divider. */
export interface LayoutSegment<T> {
  /** The divider that opens this group, or null for the items before the first one. */
  divider: DividerNode | null;
  items: ItemNode<T>[];
}

/**
 * Split a flat layout into segments: an initial always-visible run (items before
 * the first divider) followed by one segment per named group divider. Lets the
 * menu animate each group open/closed with a single `<Collapse>`.
 *
 * A group left with no items — every one filtered out by role, division or
 * feature visibility — is dropped rather than rendered as a header opening onto
 * nothing.
 */
export function buildSegments<T>(layout: LayoutNode<T>[]): LayoutSegment<T>[] {
  const segments: LayoutSegment<T>[] = [{ divider: null, items: [] }];
  for (const node of layout) {
    if (node.type === 'divider') segments.push({ divider: node, items: [] });
    else segments[segments.length - 1].items.push(node);
  }
  return segments.filter((s) => s.divider === null || s.items.length > 0);
}
